import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface RadioProps {
    id: string;
    name: string;
    children: ReactNode;
    setRef?: (instance: HTMLInputElement | null) => void;
    dataTestId?: string;
    disabled?: boolean;
    size?: number;
    gap?: number;
}
type RadioInputProps = RadioProps &
    React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const Radio = ({
    id,
    name,
    children,
    checked,
    onChange,
    onClick,
    value,
    setRef,
    defaultChecked,
    dataTestId,
    disabled,
    size = 2,
    gap = 3,
}: RadioInputProps) => {
    return (
        <Container $gap={gap}>
            <RadioWrapper disabled={disabled ?? false}>
                <Input
                    id={id}
                    disabled={disabled ?? false}
                    name={name}
                    type="radio"
                    checked={checked}
                    onChange={onChange}
                    value={value}
                    defaultChecked={defaultChecked}
                    ref={ref => setRef && setRef(ref)}
                    data-testid={dataTestId}
                />
                <Label htmlFor={id} $size={size} />
            </RadioWrapper>
            <TextWrapper onClick={onClick}>{children}</TextWrapper>
        </Container>
    );
};

const TextWrapper = styled.div`
    align-self: center;
    cursor: pointer;
`;

const Container = styled.div<{ $gap: number }>`
    display: flex;
    flex-direction: row;
    gap: ${props => props.$gap}rem;
`;

const RadioWrapper = styled.div<{ disabled?: boolean }>`
    opacity: ${props => (props.disabled ? 0.5 : 1)};
    pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
    position: relative;
`;

const Label = styled.label<{ $size: number }>`
    background-color: ${props => props.theme.colors.light};
    border: 1px solid ${props => props.theme.colors.input.border};
    border-radius: 50%;
    cursor: pointer;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    height: ${props => props.$size}rem;
    width: ${props => props.$size}rem;

    &:after {
        border: ${props => props.$size * 0.15}rem solid ${props => props.theme.colors.light};
        border-top: none;
        border-right: none;
        content: '';
        height: ${props => props.$size * 0.275}rem;
        opacity: 0;
        position: absolute;
        top: ${props => props.$size * 0.2}rem;
        left: ${props => props.$size * 0.2}rem;
        transform: rotate(-45deg);
        width: ${props => props.$size * 0.45}rem;
    }
`;

const Input = styled.input`
    visibility: hidden;
    display: none;
    &:checked + ${Label} {
        background-color: ${props => props.theme.colors.primary};
        border-color: ${props => props.theme.colors.primary};
        transition: 0.3s;

        &::after {
            opacity: 1;
        }
    }
`;

export default Radio;
